<script>
  // Prefix for the ids that tie each field to its visible label.
  const fid = `f${Math.random().toString(36).slice(2, 8)}`;
  import Layout from '../components/Layout.svelte';
  import CustomerAutocomplete from '../components/CustomerAutocomplete.svelte';
  import { companyInvoicesApi } from '../stores/api.js';
  import { activeCompanyId, activeCompany } from '../stores/company.js';
  import { push, link } from 'svelte-spa-router';
  import { t, tImmediate } from '../i18n/index.js';
  import { FilePlus, Send, ChevronLeft, ChevronRight, Eye, LayoutList, Wand2, AlertTriangle } from '../lib/icons/index.js';
  import ErrorBanner from '../components/ErrorBanner.svelte';
  import SuccessBanner from '../components/SuccessBanner.svelte';
  import InfoHint from '../components/InfoHint.svelte';
  import CorrelatiEditor from '../components/CorrelatiEditor.svelte';
  import {
    emptyCorrelato, isEmptyCorrelato, filledCorrelati, incompleteCorrelati, correlatiPayload,
  } from '../lib/correlati.js';
  import LineItemsEditor, { emptyLineItem } from '../components/LineItemsEditor.svelte';
  import InvoiceTotalsPreview from '../components/InvoiceTotalsPreview.svelte';
  import { missingNaturaLines } from '../lib/natura.js';
  import { computeInvoiceTotals, submittedLineItems } from '../lib/totals.js';
  import { invoiceWarningNotices } from '../lib/warnings.js';
  import PaymentTermsEditor from '../components/PaymentTermsEditor.svelte';
  import { buildPagamento, emptyPaymentTerms, todayCalendarDay } from '../lib/payment.js';
  import InvoiceNumberField from '../components/InvoiceNumberField.svelte';
  import { numberingPayload, numberingReady } from '../lib/numbering.js';
  import { issueViaDraft, IssueOutcomeUnknownError, emptyIssueState, issueKeyOf, acknowledgementFor } from '../lib/issue-via-draft.js';
  import { createRequestGeneration } from '../lib/request-generation.js';
  import { formatDateTime, formatEuro } from '../lib/labels.js';

  // Notices the API attached to an invoice it accepted anyway, and the id of
  // that invoice: when there is something to read, the automatic redirect is
  // cancelled and the operator opens the invoice when they are done reading.
  let warningNotices = [];
  let createdInvoiceId = '';

  // What the form remembers across clicks of «Emetti»: the draft it saved and
  // what it sent. A refused or interrupted attempt keeps it, so the next click
  // reuses that draft — or finds it already issued — instead of creating a
  // second document. See lib/issue-via-draft.js.
  let issueState = emptyIssueState();
  let keptDraftId = '';
  let alreadyIssued = null;
  // A promotion the server held back because a lookalike was issued in the
  // last 24 hours: the documents it named, until the operator answers.
  let possibleDuplicate = null;
  let duplicateCancelled = false;
  // The warning's token, set only by «Emetti comunque» for the one click it
  // answers, and only while the form is the one the warning was given for.
  let acknowledgeNext = null;

  // Every issue belongs to the company it was started for. A switch resets the
  // form's memory, and an answer that arrives afterwards is dropped — also
  // when the operator has come back to the first company in the meantime.
  const generation = createRequestGeneration();
  let issueCompanyId = $activeCompanyId;
  $: companySwitched($activeCompanyId);
  function companySwitched(companyId) {
    if (companyId === issueCompanyId) return;
    issueCompanyId = companyId;
    resetIssue();
  }
  function resetIssue() {
    generation.invalidate();
    issueState = emptyIssueState();
    keptDraftId = '';
    alreadyIssued = null;
    possibleDuplicate = null;
    duplicateCancelled = false;
    loading = false;
  }
  function issueAnyway() {
    acknowledgeNext = acknowledgementFor(possibleDuplicate, draftPayload(), promoteBase());
    handleSubmit();
  }
  // Any edit after the warning drops it with its token: the next «Emetti»
  // saves the edited draft and has it checked again.
  function formEdited() {
    if (possibleDuplicate) possibleDuplicate = null;
  }
  // The number travels only when typed by hand; otherwise the server assigns
  // the next one of the series at the moment of issue.
  function promoteBase() {
    return { invoiceDate, ...numberingPayload({ manual: manualNumber, invoiceNumber }) };
  }
  // Nothing was issued; the draft stays, and the next «Emetti» reuses it.
  function cancelIssue() {
    possibleDuplicate = null;
    duplicateCancelled = true;
    keptDraftId = issueState.draftId || '';
  }

  let mode = 'form'; // 'form' or 'wizard'
  let wizardStep = 0;
  let loading = false;
  let error = '';
  let success = '';

  // Invoice form state
  let tipoDocumento = 'TD01';
  // The number is assigned by the server when the document is issued;
  // `invoiceNumber` is only the override typed with «Indica il numero a mano».
  let manualNumber = false;
  let invoiceNumber = '';
  let invoiceDate = todayCalendarDay();
  let formatoTrasmissione = 'FPR12';
  let causale = '';
  let selectedCustomer = null;
  let codiceDestinatario = '';
  let pecDestinatario = '';

  // EsigibilitaIVA — when the VAT on this invoice becomes collectable, and by
  // whom. Empty means "not chosen": the API then applies its historical
  // default (I). On an invoice to a Pubblica Amministrazione that default is
  // almost always the wrong answer, so there the choice is demanded instead.
  const esigibilitaCodes = ['I', 'D', 'S'];
  let esigibilitaIVA = '';

  // Line items
  let lineItems = [emptyLineItem()];

  // Correlated documents (DatiOrdineAcquisto / DatiContratto / DatiConvenzione /
  // DatiRicezione / DatiFattureCollegate). Only the fields that are actually
  // filled in day to day are exposed: the API still accepts NumItem,
  // CodiceCommessaConvenzione and RiferimentoNumeroLinea, but putting them on
  // the form would bury the two codes people really come here for. The rows and
  // the body they produce live in lib/correlati.js, shared with the draft editor.
  let correlati = [emptyCorrelato()];
  let correlatiOpen = false;

  function handleTipoDocumentoChange() {
    // Pre-select "fattura collegata" for a credit note, but only while the row
    // is still untouched — never overwrite something the operator chose.
    if (tipoDocumento !== 'TD04') return;
    if (correlati.length === 1 && isEmptyCorrelato(correlati[0])) {
      correlati[0].tipo = 'fattureCollegate';
      correlati = correlati;
    }
  }

  // A row with neither a document number nor a CIG (a date or a CUP alone)
  // would be dropped silently on the way out; say so instead.
  $: correlatiIncomplete = incompleteCorrelati(correlati);
  $: correlatiFilled = filledCorrelati(correlati);

  // Payment. The four fields, the preset picker and the object they produce
  // all live in PaymentTermsEditor / lib/payment.js, shared with the draft
  // editor: a draft is promoted into an invoice, so the two screens have to
  // describe a payment in exactly the same shape or identical terms would
  // leave as two different documents.
  let paymentTerms = emptyPaymentTerms();
  // The preset itself, not just the four fields the form shows: the bank, the
  // beneficiary and the ABI/CAB/BIC it carries belong in DettaglioPagamento,
  // and were being stored where they could never reach the document.
  let selectedPaymentMethod = null;
  // Whether the preset picker has already been answered for this document.
  // Parent-held because the wizard unmounts PaymentTermsEditor on every step
  // change, and «Inserimento manuale» leaves `selectedPaymentMethod` null —
  // indistinguishable, without this, from a form nobody has touched.
  let paymentPresetChosen = false;

  function handleCustomerSelect(customer) {
    selectedCustomer = customer;
    if (customer) {
      codiceDestinatario = customer.codiceDestinatario || '0000000';
      pecDestinatario = customer.pecDestinatario || '';
      paymentTerms = {
        ...paymentTerms,
        modalitaPagamento: customer.modalitaPagamento || paymentTerms.modalitaPagamento,
        iban: customer.iban || paymentTerms.iban,
        giorni: customer.scadenzaGiorni || paymentTerms.giorni,
      };
      // The choice recorded once on the anagrafica, so a PA under split
      // payment does not have to be remembered invoice by invoice.
      if (customer.esigibilitaIva) esigibilitaIVA = customer.esigibilitaIva;
    }
  }

  // Totals. One computation, shared with the autofattura editor and identical
  // to the one the backend runs on the way to the XML — see lib/totals.js.
  // Anything else and the figures approved here are not the figures sent.
  // The document-level esigibilità goes in because it is part of how the
  // riepiloghi are grouped, and the payload below is built from the very rows
  // that were summed.
  //
  // `documentEsigibilita` is the code the *document* will carry, not the one
  // the select shows: when the field is left unset the endpoint falls back to
  // the customer's own choice, so grouping by anything else here would be
  // grouping by a code the document does not use.
  $: documentEsigibilita = esigibilitaIVA || selectedCustomer?.esigibilitaIva || '';
  $: submittedLines = submittedLineItems(lineItems);
  $: totals = computeInvoiceTotals(lineItems, { esigibilitaIVA: documentEsigibilita });

  // Lines that are zero-rated but still missing their Natura code.
  $: missingNatura = missingNaturaLines(lineItems);

  /**
   * The document as a draft: everything but the number and the date, which
   * are given at the moment of issue. Every optional field and every
   * correlated block is named, `null` when empty — the kept draft is updated
   * with PATCH, which replaces only what it is sent, so a field cleared on the
   * form has to be cleared on the draft too.
   */
  function draftPayload() {
    return {
      customerId: selectedCustomer?.id || null,
      totalAmount: totals.totaleText,
      formatoTrasmissione,
      codiceDestinatario: codiceDestinatario || '0000000',
      pecDestinatario: pecDestinatario || null,
      tipoDocumento,
      causale: causale || null,
      esigibilitaIVA: esigibilitaIVA || null,
      ...correlatiPayload(correlati),
      lineItems: submittedLines,
      // The invoice date is the anchor the terms are counted from, and it is
      // on screen here — which is exactly what a draft does not have.
      pagamento: buildPagamento({
        terms: paymentTerms,
        importoPagamento: totals.pagabileText,
        anchorDate: invoiceDate,
        method: selectedPaymentMethod,
      }),
    };
  }

  async function handleSubmit() {
    error = '';
    success = '';
    warningNotices = [];
    createdInvoiceId = '';
    keptDraftId = '';
    alreadyIssued = null;
    possibleDuplicate = null;
    duplicateCancelled = false;
    const acknowledgement = acknowledgeNext;
    acknowledgeNext = null;
    if (!numberingReady({ manual: manualNumber, invoiceNumber })) { error = tImmediate('numbering.manualEmpty'); return; }
    if (lineItems.every(item => !item.descrizione)) { error = tImmediate('invoiceCreate.errors.atLeastOneLine'); return; }
    // A 0%-VAT line must declare its Natura, otherwise SdI rejects the document.
    if (missingNatura.length > 0) {
      error = tImmediate('naturaIva.missingOnLines', { lines: missingNatura.join(', ') });
      return;
    }
    // A PA invoice is where the silent default does real damage, and this is
    // the last moment anybody can catch it.
    if (formatoTrasmissione === 'FPA12' && !esigibilitaIVA) {
      error = tImmediate('invoiceCreate.errors.esigibilitaRequiredPA');
      return;
    }
    if (correlatiIncomplete.length > 0) {
      error = tImmediate('invoiceCreate.errors.correlatoIdRequired', { n: correlatiIncomplete.join(', ') });
      correlatiOpen = true;
      return;
    }

    loading = true;
    const gen = generation.begin();
    const payload = draftPayload();
    const base = promoteBase();
    try {
      const outcome = await issueViaDraft({
        api: {
          createDraft: companyInvoicesApi.createDraft,
          updateDraft: companyInvoicesApi.updateDraft,
          deleteDraft: companyInvoicesApi.deleteDraft,
          promoteDraft: companyInvoicesApi.promoteDraft,
          getInvoice: companyInvoicesApi.get,
        },
        companyId: $activeCompanyId,
        state: issueState,
        payload,
        promoteBody: { ...base, ...(acknowledgement && { acknowledgeDuplicates: acknowledgement }) },
      });
      if (!generation.isCurrent(gen)) return;
      issueState = outcome.state;
      if (outcome.outcome === 'possible_duplicate') {
        possibleDuplicate = {
          matches: outcome.matches,
          windowHours: outcome.windowHours ?? 24,
          token: outcome.duplicateCheckToken,
          formKey: issueKeyOf(payload, base),
        };
      } else if (outcome.outcome === 'issued') {
        const result = outcome.result;
        // The server's number, never the preview: another document of the
        // series may have been issued in between.
        success = tImmediate(result.awaitingManualSignature
          ? 'invoiceCreate.successMessageAwaitingSignature'
          : 'invoiceCreate.successMessage', { invoiceNumber: result.invoiceNumber, id: result.id });
        warningNotices = invoiceWarningNotices(result.warnings);
        createdInvoiceId = result.id;
        // Navigate to invoice detail after short delay — unless there is a
        // notice to read, which a redirect would take off the screen before
        // anybody had the chance.
        if (warningNotices.length === 0) {
          setTimeout(() => push(`/invoices/${result.id}`), 1500);
        }
      } else {
        alreadyIssued = { invoiceId: outcome.invoiceId, changesDiscarded: outcome.changesDiscarded };
        // Discarded edits are worth reading before leaving the form.
        if (!outcome.changesDiscarded) {
          setTimeout(() => push(`/invoices/${outcome.invoiceId}`), 1500);
        }
      }
    } catch (err) {
      if (!generation.isCurrent(gen)) return;
      if (err instanceof IssueOutcomeUnknownError) {
        // Nothing new was written; the next click checks the kept draft first.
        issueState = err.state;
        error = tImmediate('form.issue.outcomeUnknown');
      } else {
        if (err?.state) issueState = err.state;
        keptDraftId = err?.draftId || '';
        // Keep the Error object so ErrorBanner maps it to a friendly message;
        // validation paths above assign already-localized strings.
        error = err;
      }
    }
    loading = false;
  }

  // Document type codes; labels are looked up at render time via $t() so they
  // stay in sync with the active locale.
  const tipoDocumentoCodes = ['TD01', 'TD02', 'TD03', 'TD04', 'TD05', 'TD06', 'TD24', 'TD25'];

  const wizardStepKeys = ['recipient', 'lineItems', 'payment', 'summary'];

  function nextStep() { if (wizardStep < wizardStepKeys.length - 1) wizardStep++; }
  function prevStep() { if (wizardStep > 0) wizardStep--; }

  // Input CSS helper
  const inputCls = "input";
  const inputStyle = "";
  const labelCls = "block eyebrow mb-1";
  const labelStyle = "color: var(--color-text-muted);";
</script>

<Layout>
  <div class="max-w-5xl mx-auto">
    <div class="flex items-center justify-between mb-6">
      <div class="flex items-center gap-3">
        <h1 class="section-title mb-0">{$t('invoiceCreate.title')}</h1>
      </div>
      <div class="segmented" role="group">
        <button on:click={() => { mode = 'form'; }} type="button" aria-pressed={mode === 'form'}>
          <LayoutList size={14} class="inline mr-1" />{$t('invoiceCreate.modeForm')}
        </button>
        <button on:click={() => { mode = 'wizard'; wizardStep = 0; }} type="button" aria-pressed={mode === 'wizard'}>
          <Wand2 size={14} class="inline mr-1" />{$t('invoiceCreate.modeWizard')}
        </button>
      </div>
    </div>

    <ErrorBanner error={error} raw={typeof error === 'string'} />

    <!-- The server held the promotion back: a document just like this one was
         issued in the last 24 hours. Nothing is issued until the operator says. -->
    {#if possibleDuplicate}
      <div class="alert alert-accent mb-4" role="alert">
        <AlertTriangle size={18} class="alert-mark mt-0.5" />
        <div class="min-w-0 flex-1">
          <p class="text-sm measure" style="color: var(--color-text-body);">{$t('form.issue.possibleDuplicate', { ore: possibleDuplicate.windowHours })}</p>
          <ul class="mt-2 space-y-1 text-sm" style="color: var(--color-text-body);">
            {#each possibleDuplicate.matches as match (match.id)}
              <li>
                {$t('form.issue.possibleDuplicateMatch', { numero: match.invoiceNumber, emessoIl: formatDateTime(match.issuedAt), totale: formatEuro(match.totalAmount) })}
                <a href={`/invoices/${match.id}`} use:link class="ml-2 eyebrow text-action">{$t('invoiceWarnings.openInvoice')}</a>
              </li>
            {/each}
          </ul>
          <p class="text-xs measure mt-2" style="color: var(--color-text-body);">{$t('form.issue.possibleDuplicateQuestion')}</p>
          <div class="flex flex-wrap gap-2 mt-3">
            <button type="button" on:click={issueAnyway} disabled={loading} class="btn btn-primary btn-sm">{$t('form.issue.issueAnyway')}</button>
            <button type="button" on:click={cancelIssue} disabled={loading} class="btn btn-secondary btn-sm">{$t('form.issue.cancelIssue')}</button>
          </div>
        </div>
      </div>
    {/if}

    <!-- A refusal leaves nothing issued, and nothing lost: the data is in a
         draft, which the next click reuses instead of creating another. -->
    {#if keptDraftId}
      <div class="alert alert-accent mb-4" role="status">
        <AlertTriangle size={18} class="alert-mark mt-0.5" />
        <div class="min-w-0 flex-1">
          <p class="text-xs measure" style="color: var(--color-text-body);">{$t(duplicateCancelled ? 'form.issue.duplicateCancelled' : 'form.issue.draftKept')}</p>
          <a href={`/drafts/${keptDraftId}`} use:link class="inline-block mt-2 eyebrow text-action">
            {$t('form.issue.draftKeptLink')}
          </a>
        </div>
      </div>
    {/if}

    <!-- An earlier click had already issued this document. When the form has
         changed since, say so: those edits are not in what was sent. -->
    {#if alreadyIssued}
      <div class="alert alert-accent mb-4" role="status" aria-live="polite">
        <AlertTriangle size={18} class="alert-mark mt-0.5" />
        <div class="min-w-0 flex-1">
          {#if alreadyIssued.changesDiscarded}
            <p class="text-sm measure" style="color: var(--color-text-body);">{$t('form.issue.alreadyIssuedChangesDiscarded')}</p>
          {:else}
            <p class="text-sm measure" style="color: var(--color-text-body);">{$t('form.issue.alreadyIssued')}</p>
          {/if}
          <a href={`/invoices/${alreadyIssued.invoiceId}`} use:link class="inline-block mt-3 eyebrow text-action">
            {$t('invoiceWarnings.openInvoice')}
          </a>
        </div>
      </div>
    {/if}
    <!-- role=status + no monospace: this confirmation fires immediately before
         an automatic redirect, so a screen-reader user would otherwise be moved
         to another page without ever being told the invoice was created. -->
    {#if success}
      <SuccessBanner message={success} />
    {/if}

    <!-- Not an error: the invoice exists and has been queued. It is the one
         thing about it the operator would otherwise never be told. -->
    {#if warningNotices.length > 0}
      <div class="alert alert-accent mb-4" role="status" aria-live="polite">
        <AlertTriangle size={18} class="alert-mark mt-0.5" />
        <div class="min-w-0 flex-1">
          <p class="text-sm font-bold" style="color: var(--color-text-heading);">{$t('invoiceWarnings.title')}</p>
          {#each warningNotices as notice}
            <p class="mt-2 text-xs measure" style="color: var(--color-text-body);">{notice.text}</p>
            {#if notice.detail}
              <p class="mt-1 text-xs measure" style="color: var(--color-text-muted);">{notice.detail}</p>
            {/if}
          {/each}
          {#if createdInvoiceId}
            <a href={`/invoices/${createdInvoiceId}`} use:link class="inline-block mt-3 eyebrow text-action">
              {$t('invoiceWarnings.openInvoice')}
            </a>
          {/if}
        </div>
      </div>
    {/if}

    {#if mode === 'wizard'}
      <!-- Wizard Progress -->
      <div class="flex items-center gap-2 mb-6">
        {#each wizardStepKeys as stepKey, i}
          <div class="flex items-center gap-2">
            <div class="w-8 h-8 flex items-center justify-center text-xs font-bold {i <= wizardStep ? 'bg-action text-on-action' : 'border-w'}"
              style={i > wizardStep ? 'border-color: var(--color-tint-medium); color: var(--color-text-muted);' : ''}>
              {i + 1}
            </div>
            <span class="eyebrow hidden sm:inline" style="color: {i <= wizardStep ? 'var(--color-primary)' : 'var(--color-text-muted)'};">{$t(`invoiceCreate.wizard.step.${stepKey}`)}</span>
            {#if i < wizardStepKeys.length - 1}
              <div class="w-8 h-0.5 {i < wizardStep ? 'bg-action' : ''}" style={i >= wizardStep ? 'background: var(--color-tint-medium);' : ''}></div>
            {/if}
          </div>
        {/each}
      </div>
    {/if}

    <form on:submit|preventDefault={handleSubmit} on:input={formEdited} on:change={formEdited}>
      <!-- Section: Tipo & Destinatario -->
      {#if mode === 'form' || (mode === 'wizard' && wizardStep === 0)}
        <div class="card p-6 mb-4">
          <h2 class="eyebrow eyebrow-lg mb-4" style="color: var(--color-primary);">{$t('invoiceCreate.sections.docAndRecipient')}</h2>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <span id="{fid}-1" class="{labelCls} inline-flex items-center gap-1" style={labelStyle}>{$t('invoiceCreate.fields.tipoDocumento')}<InfoHint term="tipoDocumento" /></span>
              <select aria-labelledby="{fid}-1" bind:value={tipoDocumento} on:change={handleTipoDocumentoChange} class={inputCls} style={inputStyle}>
                {#each tipoDocumentoCodes as code}<option value={code}>{code} - {$t(`invoiceCreate.tipiDocumento.${code}`)}</option>{/each}
              </select>
            </div>
            <div>
              <InvoiceNumberField companyId={$activeCompanyId} {tipoDocumento} {formatoTrasmissione} {invoiceDate} bind:manual={manualNumber} bind:invoiceNumber />
            </div>
            <div>
              <span id="{fid}-2" class={labelCls} style={labelStyle}>{$t('invoiceCreate.fields.date')}</span>
              <input aria-labelledby="{fid}-2" bind:value={invoiceDate} type="date" class={inputCls} style={inputStyle} />
            </div>
          </div>

          <div class="mb-4">
            <span class={labelCls} style={labelStyle}>{$t('invoiceCreate.fields.customerAutocomplete')}</span>
            <!-- An invoice is issued to a cliente; dual-role records match too. -->
            <CustomerAutocomplete kind="cliente" bind:value={selectedCustomer} onSelect={handleCustomerSelect} />
          </div>

          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <span id="{fid}-3" class="{labelCls} inline-flex items-center gap-1" style={labelStyle}>{$t('invoiceCreate.fields.codiceDestinatario')}<InfoHint term="codiceDestinatario" /></span>
              <input aria-labelledby="{fid}-3" bind:value={codiceDestinatario} maxlength="7" class={inputCls} style={inputStyle} />
            </div>
            <div>
              <span id="{fid}-4" class="{labelCls} inline-flex items-center gap-1" style={labelStyle}>{$t('invoiceCreate.fields.pecDestinatario')}<InfoHint term="pec" /></span>
              <input aria-labelledby="{fid}-4" bind:value={pecDestinatario} type="email" class={inputCls} style={inputStyle} />
            </div>
            <div>
              <span id="{fid}-5" class={labelCls} style={labelStyle}>{$t('invoiceCreate.fields.format')}</span>
              <select aria-labelledby="{fid}-5" bind:value={formatoTrasmissione} class={inputCls} style={inputStyle}>
                <option value="FPR12">{$t('invoiceCreate.formats.FPR12')}</option>
                <option value="FPA12">{$t('invoiceCreate.formats.FPA12')}</option>
              </select>
            </div>
          </div>

          <!-- EsigibilitaIVA. Optional on a B2B invoice, demanded on an FPA12:
               split payment is the ordinary case for a Pubblica
               Amministrazione and the one nobody remembers to declare. -->
          <div class="mt-4">
            <span id="{fid}-6" class="{labelCls} inline-flex items-center gap-1" style={labelStyle}>
              {formatoTrasmissione === 'FPA12'
                ? $t('invoiceCreate.fields.esigibilitaRequired')
                : $t('invoiceCreate.fields.esigibilita')}
              <InfoHint text={$t('invoiceCreate.esigibilita.hint')} />
            </span>
            <select aria-labelledby="{fid}-6" bind:value={esigibilitaIVA} class={inputCls} style={inputStyle}>
              <option value="">{$t('invoiceCreate.esigibilita.unset')}</option>
              {#each esigibilitaCodes as code}
                <option value={code}>{$t(`invoiceCreate.esigibilita.options.${code}`)}</option>
              {/each}
            </select>
            <p class="mt-2 text-xs measure" style="color: var(--color-text-muted);">
              {esigibilitaIVA
                ? $t(`invoiceCreate.esigibilita.explain.${esigibilitaIVA}`)
                : $t('invoiceCreate.esigibilita.intro')}
            </p>
            {#if formatoTrasmissione === 'FPA12'}
              <div class="alert alert-accent mt-3">
                <AlertTriangle size={18} class="alert-mark mt-0.5" />
                <p class="text-xs measure" style="color: var(--color-text-body);">{$t('invoiceCreate.esigibilita.paWarning')}</p>
              </div>
            {/if}
          </div>

          {#if mode === 'form'}
            <div class="mt-4">
              <span id="{fid}-7" class={labelCls} style={labelStyle}>{$t('invoiceCreate.fields.causale')}</span>
              <input aria-labelledby="{fid}-7" bind:value={causale} class={inputCls} style={inputStyle} placeholder={$t('invoiceCreate.fields.causalePlaceholder')} />
            </div>
          {/if}
        </div>
      {/if}

      <!-- Section: Correlated documents.
           Lives with the document/recipient step because the two fields that
           make it necessary — TipoDocumento and FormatoTrasmissione — are the
           ones right above it, so the operator sees the section unfold the
           moment they pick TD04 or FPA12. -->
      {#if mode === 'form' || (mode === 'wizard' && wizardStep === 0)}
        <CorrelatiEditor bind:correlati bind:open={correlatiOpen} {tipoDocumento} {formatoTrasmissione} />
      {/if}

      <!-- Section: Line Items -->
      {#if mode === 'form' || (mode === 'wizard' && wizardStep === 1)}
        <div class="card p-6 mb-4">
          <h2 class="eyebrow eyebrow-lg mb-4" style="color: var(--color-primary);">{$t('invoiceCreate.sections.lineItems')}</h2>

          <LineItemsEditor bind:lineItems {totals} />
        </div>
      {/if}

      <!-- Section: Payment -->
      {#if mode === 'form' || (mode === 'wizard' && wizardStep === 2)}
        <div class="card p-6 mb-4">
          <h2 class="eyebrow eyebrow-lg mb-4" style="color: var(--color-primary);">{$t('invoiceCreate.sections.payment')}</h2>

          <PaymentTermsEditor
            bind:terms={paymentTerms}
            bind:method={selectedPaymentMethod}
            bind:presetChosen={paymentPresetChosen}
            companyId={$activeCompanyId}
            anchorDate={invoiceDate}
          />

          {#if mode === 'form'}
            <div class="mt-4">
              <span id="{fid}-8" class={labelCls} style={labelStyle}>{$t('invoiceCreate.fields.causale')}</span>
              <input aria-labelledby="{fid}-8" bind:value={causale} class={inputCls} style={inputStyle} placeholder={$t('invoiceCreate.fields.causalePlaceholder')} />
            </div>
          {/if}
        </div>
      {/if}

      <!-- Section: Summary / Preview -->
      {#if mode === 'form' || (mode === 'wizard' && wizardStep === 3)}
        <div class="card p-6 mb-4">
          <h2 class="eyebrow eyebrow-lg mb-4" style="color: var(--color-primary);">
            <Eye size={16} class="inline mr-1" /> {$t('invoiceCreate.sections.summary')}
          </h2>

          <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div>
              <span class="text-xs" style="color: var(--color-text-muted);">{$t('invoiceCreate.summary.type')}</span>
              <p class="font-mono font-bold" style="color: var(--color-text-body);">{tipoDocumento}</p>
            </div>
            <div>
              <span class="text-xs" style="color: var(--color-text-muted);">{$t('invoiceCreate.summary.number')}</span>
              <p class="font-mono font-bold" style="color: var(--color-text-body);">{manualNumber ? (invoiceNumber || '-') : $t('numbering.summaryAuto')}</p>
            </div>
            <div>
              <span class="text-xs" style="color: var(--color-text-muted);">{$t('invoiceCreate.summary.date')}</span>
              <p class="font-mono font-bold" style="color: var(--color-text-body);">{invoiceDate}</p>
            </div>
            <div>
              <span class="text-xs" style="color: var(--color-text-muted);">{$t('invoiceCreate.summary.recipient')}</span>
              <p class="font-mono font-bold truncate" style="color: var(--color-text-body);">
                {selectedCustomer ? (selectedCustomer.ragioneSociale || `${selectedCustomer.nome} ${selectedCustomer.cognome}`) : codiceDestinatario || '-'}
              </p>
            </div>
          </div>

          <!-- Correlated references: in wizard mode the section lives two steps
               back, so the recap is the last chance to notice a missing CIG. -->
          {#if correlatiFilled.length > 0}
            <div class="border-t pt-3 mb-2" style="border-color: var(--color-tint-soft);">
              <span class="eyebrow" style="color: var(--color-text-muted);">{$t('invoiceCreate.correlati.summaryTitle')}</span>
              <ul class="mt-1 space-y-1">
                {#each correlatiFilled as ref}
                  <li class="text-sm font-mono" style="color: var(--color-text-body);">
                    {$t(`invoiceCreate.correlati.types.${ref.tipo}`)}: {ref.idDocumento || ref.codiceCIG}{ref.data ? ` · ${ref.data}` : ''}{ref.codiceCIG ? ` · ${$t('invoices.correlati.fields.codiceCIG')} ${ref.codiceCIG}` : ''}{ref.codiceCUP ? ` · ${$t('invoices.correlati.fields.codiceCUP')} ${ref.codiceCUP}` : ''}
                  </li>
                {/each}
              </ul>
            </div>
          {/if}

          <!-- IVA breakdown -->
          <InvoiceTotalsPreview {totals} />
        </div>
      {/if}

      <!-- Actions -->
      <div class="flex items-center justify-between">
        {#if mode === 'wizard'}
          <div class="flex gap-2">
            {#if wizardStep > 0}
              <button type="button" on:click={prevStep} class="btn btn-secondary btn-sm gap-1">
                <ChevronLeft size={14} /> {$t('common.back')}
              </button>
            {/if}
            {#if wizardStep < wizardStepKeys.length - 1}
              <button type="button" on:click={nextStep} class="btn btn-primary btn-sm gap-1">
                {$t('common.next')} <ChevronRight size={14} />
              </button>
            {/if}
          </div>
        {:else}
          <div></div>
        {/if}

        {#if mode === 'form' || (mode === 'wizard' && wizardStep === wizardStepKeys.length - 1)}
          <div class="flex flex-col items-end gap-2">
            <button type="submit" disabled={loading}
              class="btn btn-primary gap-2">
              {#if loading}
                <span class="animate-pulse">{$t('app.sending')}</span>
              {:else}
                <Send size={18} />
                <span>{$t('invoiceCreate.issue.button')}</span>
              {/if}
            </button>
            <p class="text-xs measure text-right" style="color: var(--color-text-muted);">{$t('invoiceCreate.issue.explainer')}</p>
          </div>
        {/if}
      </div>
    </form>
  </div>
</Layout>
